<template>
    <div ref="loader">

        <!-- Host group name and refresh button -->
        <div class="w-100 bg-no-status-gray shadow-lg-no-status-gray text-white">
            <b-container class="container-lg">
                <b-row class="p-0 pt-2">
                    <b-col class="col-9 text-start min-width-0">
                        <h5 class="text-truncate">
                            <i class="fa-solid fa-chevron-left" @click="goBack"></i>
                            {{ container.name }}
                        </h5>
                    </b-col>
                    <b-col class="col-3 text-end">
                        <button type="button" class="btn btn-sm btn-secondary" @click="refreshData">
                            <i class="fa-solid fa-arrows-rotate"
                               :class="{'fa-spin': loading}"></i>
                        </button>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col class="col-12">
                        {{ hostgroup.description }}
                    </b-col>
                </b-row>

                <!-- Host status filter buttons -->
                <b-row class="p-0 pt-2 pb-2">
                    <b-col>
                        <i class="fa-solid fa-filter"></i>
                        {{ $t('Host states filter') }}
                    </b-col>
                    <b-col class="col-12 pt-2">
                        <b-button-group class="w-100">
                            <b-button variant="success" @click="toHosts('up')">
                                {{ statusSummary.up ?? 0 }}
                            </b-button>
                            <b-button variant="danger" @click="toHosts('down')">
                                {{ statusSummary.down ?? 0 }}
                            </b-button>
                            <b-button variant="secondary" @click="toHosts('unreachable')">
                                {{ statusSummary.unreachable ?? 0 }}
                            </b-button>
                        </b-button-group>
                    </b-col>
                </b-row>
            </b-container>
        </div>
        <!-- End of host group header -->

        <!-- List all hosts inside of this host group -->
        <b-container class="container-lg">
            <b-row class="p-0 pt-2">
                <b-col class="text-start">
                    <h5><i class="fa-solid fa-desktop"></i> {{ $t('Hosts') }}</h5>
                </b-col>
            </b-row>

            <!-- Start actual list -->
            <b-list-group
                v-for="host in hosts"
                v-bind:key="host.Host.id"
                class="mt-2">

                <div class="flex-column align-items-start">
                    <div class="list-group-item flex-column align-items-start list-group-item-action ps-0">

                        <div class="d-flex flex-row min-h-110">
                            <div class="p-2">
                                <Hoststatus v-bind:current_state="host.Hoststatus.currentState"
                                            v-bind:human_state="host.Hoststatus.humanState"></Hoststatus>
                            </div>
                            <div class="min-width-0">
                                <div class="row p-2">
                                    <div class="col-12 text-truncate h5">
                                        <router-link :to="{name: 'hostsDetails', params: {id: host.Host.id}}">
                                            {{ host.Host.name }}
                                        </router-link>
                                    </div>
                                    <div class="col-12 text-truncate">
                                        <div class="row">
                                            <div class="col-12 text-truncate">
                                                {{ host.Host.address }}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="row p-2">
                                    <div class="col-12">
                                        <div v-if="host.Hoststatus.currentState > 0 && host.Hoststatus.problemHasBeenAcknowledged">
                                            <i class="far fa-user"></i> {{ $t('Host is acknowledged') }}
                                        </div>
                                        <div v-if="host.Hoststatus.currentState > 0 && !host.Hoststatus.problemHasBeenAcknowledged">
                                            <i class="far fa-user"></i> {{ $t('Host is not acknowledged') }}
                                        </div>
                                    </div>
                                    <div class="col-12">
                                        <div v-if="host.Hoststatus.scheduledDowntimeDepth > 0">
                                            <i class="fa fa-power-off"></i> {{ $t('Host is in downtime') }}
                                        </div>
                                    </div>
                                </div>
                                <!-- works but I think it is to much for this list
                                <div class="row">
                                    <div class="col-12 text-end">
                                        <date-switch v-bind:css_class="host.Hoststatus.humanState"
                                                     v-bind:status="host.Hoststatus"></date-switch>
                                    </div>
                                </div>
                                -->

                                <div class="row p-2">
                                    <div class="col-12 pb-2">
                                        {{ $t('Service state overview') }}
                                    </div>
                                    <div class="col-3">
                                        <b-button block variant="success" size="sm" class="w-100"
                                                  @click="toServices(host.Host.id, 'ok')">
                                            {{ host.ServicestatusSummary.state.ok }}
                                        </b-button>
                                    </div>
                                    <div class="col-3">
                                        <b-button block variant="warning" size="sm" class="w-100"
                                                  @click="toServices(host.Host.id, 'warning')">
                                            {{ host.ServicestatusSummary.state.warning }}
                                        </b-button>
                                    </div>
                                    <div class="col-3">
                                        <b-button block variant="danger" size="sm" class="w-100"
                                                  @click="toServices(host.Host.id, 'critical')">
                                            {{ host.ServicestatusSummary.state.critical }}
                                        </b-button>
                                    </div>
                                    <div class="col-3">
                                        <b-button block variant="secondary" size="sm" class="w-100"
                                                  @click="toServices(host.Host.id, 'unknown')">
                                            {{ host.ServicestatusSummary.state.unknown }}
                                        </b-button>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </b-list-group>
            <!-- end new list -->
            <!-- end of actual list -->

            <!-- Paginator -->
            <b-list-group class="mt-3" ref="noMass">
                <div v-if="hosts.length > 0">
                    <div class="d-flex w-100 justify-content-between">
                        <small>{{ $t('page') }} {{ paging.page }}</small>
                        <small>{{ $t('pagesize') }} {{ paging.limit }}</small>
                    </div>
                    <div class="d-flex w-100 justify-content-center mt-4">

                        <vue-awesome-paginate
                            :total-items="paging.count"
                            :items-per-page="25"
                            :max-pages-shown="5"
                            v-model="paging.page"
                            @click="getData"
                        />
                    </div>
                </div>
                <b-list-group-item v-if="hosts.length === 0">
                    <h5 class="mb-1">{{ $t('noitems') }}</h5>
                </b-list-group-item>
            </b-list-group>
            <!-- End of Paginator -->


        </b-container>
        <!-- End of list -->

    </div>

</template>
<script>
import Priority from "../utils/Priority.vue";
import DateSwitch from "../utils/DateSwitch.vue";
import Hoststatus from "../utils/Hoststatus.vue";
import {VueAwesomePaginate} from "vue-awesome-paginate";
import { BListGroupItem } from 'bootstrap-vue-next';
import router from '../../router';

export default {
    name: "HostgroupsDetails",
    components: {BListGroupItem, VueAwesomePaginate, Hoststatus, DateSwitch, Priority},
    props: ['id', 'state'],
    data(){
        return {
            init: false,
            loading: true,
            requestStatus: false,
            hostgroup: {},
            container: {},
            statusSummary: {},
            hosts: [],
            cumulatedState: -1,
            hostsFilter: {
                filter: {
                    current_state: [],
                }
            },
            servicesFilter: {
                filter: {
                    hostsId: [],
                    current_state: [],
                }
            },
            paging: {
                page: 1,
                pageCount: 1,
            },
        }
    },
    async mounted(){
        await this.getData();
    },
    methods: {
        getData: function(page = 1){
            let loader = this.$loading.show();
            this.loading = true;
            axios.post('/hostgroups/details.json', null, {
                params: {
                    id: this.id,
                    page: page,
                    filter: this.hostsFilter
                }
            })
                .then(response => {
                    this.requestStatus = true;
                    this.hostgroup = response.data.hostgroupDetails.hostgroup.Hostgroup ?? {};
                    this.container = response.data.hostgroupDetails.hostgroup.Hostgroup.container ?? {};
                    if(this.init === false){
                        this.statusSummary = response.data.hostgroupDetails.hostgroup.StatusSummary ?? {};
                    }
                    this.hosts = response.data.hostgroupDetails.hostgroup.Hosts ?? [];
                    this.paging = response.data.hostgroupDetails.paging ?? {
                        page: 1,
                        pageCount: 1,
                    };
                    this.init = true;
                })
                .catch(error => {
                    console.log(error);
                    this.$notify({
                        type: 'error',
                        title: this.$t('error'),
                    });
                })
                .finally(() => {
                    loader.hide();
                    this.loading = false;
                });
        },
        refreshData(){
            this.hostsFilter.filter.current_state = [];
            this.getData();
        },
        goBack: function(){
            this.$router.back();
        },

        toServices: function(host, servicestate){
            this.servicesFilter.filter.hostsId = [host];
            this.servicesFilter.filter.current_state = [servicestate];
            this.$store.commit('setServicesIndexFilter', this.servicesFilter);
            router.push({name: 'servicesIndexFilter'});
            //this.$router.push({name: 'servicesIndexFilter', params: {filter: JSON.stringify(this.servicesFilter)}})
        },

        toHosts: function(state){
            this.hostsFilter.filter.current_state = [state];
            this.getData();
        },
    }
}
</script>


<style scoped>

.flex-right {
    margin-left: auto;
}

.w-90 {
    width: 90% !important;
}

</style>
